// src/components/OrientationCarousel.js
import React, { useMemo, useRef, useState } from "react";
import { CATEGORY_TILE_PNG, pickSessionCards } from "../data/orientationCards";
import { markOrientationDismissed } from "../utils/orientationGate";

/**
 * OrientationCarousel - Overlay-only, never blocks browse
 * 
 * Props:
 * - onComplete: called when user finishes all cards
 * - onSkip: called when user skips
 * 
 * Behavior:
 * - Shows 3 random cards from locked pool
 * - NO DUPLICATE TILES per session (each card uses different category PNG)
 * - No auto-advance
 * - Skippable instantly
 * - OVERLAY only - pointerEvents: none on container
 */
export default function OrientationCarousel({ onComplete, onSkip }) {
  const [index, setIndex] = useState(0);
  
  // Pick 3 unique cards with NO DUPLICATE TILES (stable across renders)
  const sessionCards = useMemo(() => pickSessionCards(3), []);

  const total = sessionCards.length || 0;
  const current = sessionCards[index];

  // basic touch swipe
  const startX = useRef(null);
  function onTouchStart(e) {
    startX.current = e.touches?.[0]?.clientX ?? null;
  }
  function onTouchEnd(e) {
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    if (startX.current == null || endX == null) return;
    const dx = endX - startX.current;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next();
    else prev();
  }

  function handleSkip() {
    markOrientationDismissed();
    onSkip?.();
  }
  
  function handleComplete() {
    markOrientationDismissed();
    onComplete?.();
  }
  
  function next() {
    setIndex((i) => Math.min(i + 1, total - 1));
  }
  function prev() {
    setIndex((i) => Math.max(i - 1, 0));
  }

  if (!current) return null;

  // Get tile PNG from category (NOT pose)
  const imgSrc = CATEGORY_TILE_PNG[current.category];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none", // CRITICAL: non-blocking overlay
        zIndex: 1000,
      }}
      aria-label="LikeKind orientation"
    >
      {/* Card container */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: 16,
          transform: "translateX(-50%)",
          width: "88vw",
          maxWidth: 420,
          borderRadius: 18,
          padding: 18,
          pointerEvents: "auto", // Only the card itself captures events
          background: "rgba(20, 20, 20, 0.85)",
          backdropFilter: "blur(12px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Skip button */}
        <button
          onClick={handleSkip}
          aria-label="Skip"
          style={{
            position: "absolute",
            right: 12,
            top: 12,
            border: "none",
            background: "rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.7)",
            fontSize: 12,
            padding: "4px 10px",
            borderRadius: 12,
            cursor: "pointer",
          }}
        >
          Skip
        </button>

        {/* Illustration - Canonical LikeKind people */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 24, paddingBottom: 8 }}>
          <img
            src={imgSrc}
            alt=""
            style={{ width: "60%", height: "auto", opacity: 0.95 }}
            draggable={false}
          />
        </div>

        {/* Text */}
        <div style={{ paddingTop: 12, color: "white", textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            {current.headline}
          </div>
          <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8, lineHeight: 1.5 }}>
            {current.body}
          </div>
        </div>

        {/* Dots */}
        <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 16 }}>
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: i === index ? "white" : "rgba(255,255,255,0.3)",
                display: "inline-block",
              }}
            />
          ))}
        </div>

        {/* Controls */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
          <button
            onClick={prev}
            disabled={index === 0}
            style={{
              opacity: index === 0 ? 0.3 : 1,
              border: "none",
              background: "transparent",
              color: "white",
              cursor: index === 0 ? "default" : "pointer",
              fontSize: 14,
              padding: "8px 12px",
            }}
          >
            Back
          </button>

          {index < total - 1 ? (
            <button
              onClick={next}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "10px 20px",
                background: "rgba(255,255,255,0.15)",
                color: "white",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleComplete}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "10px 20px",
                background: "#4CAF50",
                color: "white",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
